import { useCallback, useMemo, useState } from "react"
import { extractProperty } from "utils"

export const useFileDeliveryLogic = (
  fileManagementSteps: any,
  allBuildings: any,
  cmsCities?: any[],
  cmsProjects?: any[],
) => {
  const [value, setValue] = useState(0)
  const [selectedCity, setSelectedCity] = useState<string | null>(null)
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null)
  const [filteredFiles, setFilteredFiles] = useState<any[]>([])
  const [filteredByCityProject, setFilteredByCityProject] = useState<any[]>([])
  const [filteredByBuilding, setFilteredByBuilding] = useState<any[]>([])
  const [selectedDestination, setDestination] = useState<string | null>(null)
  const [groupedFiles, setGroupedFiles] = useState<any>({})
  const [hasError, setHasError] = useState(false)

  // Map English CMS node names to Hebrew display names
  const cityNameMap = useMemo(() => {
    const map = new Map<string, string>()
    if (cmsCities) {
      cmsCities.forEach((city: any) => {
        const hebrewName = extractProperty(city, "partnerName") || city.name
        if (hebrewName && city.name) {
          map.set(city.name, hebrewName)
        }
      })
    }
    return map
  }, [cmsCities])

  // Map English CMS project node names to Hebrew display names (projectTitle)
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>()
    if (cmsProjects) {
      cmsProjects.forEach((project: any) => {
        const title = extractProperty(project, "projectTitle") || project.name
        if (title && project.name) {
          map.set(project.name, title)
        }
      })
    }
    return map
  }, [cmsProjects])

  // Get city name from a file's content picker, mapped to Hebrew if available
  const getFileCity = useCallback(
    (file: any): string | null => {
      const cityPicker = extractProperty(file, "city")
      if (Array.isArray(cityPicker) && cityPicker.length > 0) {
        const englishName = cityPicker[0].name
        return cityNameMap.get(englishName) || englishName || null
      }
      if (typeof cityPicker === "string" && cityPicker) return cityPicker
      return null
    },
    [cityNameMap],
  )

  // Get project name from a file's content picker, mapped to Hebrew if available
  const getFileProject = useCallback(
    (file: any): string | null => {
      const projectPicker = extractProperty(file, "selectedProject")
      if (Array.isArray(projectPicker) && projectPicker.length > 0) {
        const englishName = projectPicker[0].name
        return projectNameMap.get(englishName) || englishName || null
      }
      if (typeof projectPicker === "string" && projectPicker) return projectPicker
      return null
    },
    [projectNameMap],
  )

  // Show cities that have file delivery projects, mapped to Hebrew via CMS
  const cities: string[] = useMemo(() => {
    if (!fileManagementSteps || !Array.isArray(fileManagementSteps)) return []
    const citySet = new Set<string>()
    fileManagementSteps.forEach((file: any) => {
      const city = getFileCity(file)
      if (city) citySet.add(city)
    })
    return Array.from(citySet).sort()
  }, [fileManagementSteps, getFileCity])

  // Extract unique projects from files matching selected city
  const projectsForCity: string[] = useMemo(() => {
    if (!selectedCity || !fileManagementSteps || !Array.isArray(fileManagementSteps)) return []
    const projectSet = new Set<string>()
    fileManagementSteps.forEach((file: any) => {
      const city = getFileCity(file)
      if (city === selectedCity) {
        const projectValue = getFileProject(file)
        if (projectValue) projectSet.add(projectValue)
      }
    })
    return Array.from(projectSet).sort()
  }, [fileManagementSteps, selectedCity, getFileCity, getFileProject])

  const handleCityChange = (_event: any, newValue: string | null) => {
    setHasError(false)
    setSelectedCity(newValue)
    setSelectedProject(null)
  }

  const handleProjectChange = (_event: any, newValue: string | null) => {
    setHasError(false)
    setSelectedProject(newValue)
  }

  const handleBuildingChange = (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setHasError(false)
    setSelectedBuilding(event.target.value)
  }

  const handleDestinationChange = (dest: string) => {
    setHasError(false)
    setDestination(dest)
  }

  const getSelectedDisplayValues = () => {
    const selectedBuildingName = selectedBuilding
      ? extractProperty(
          allBuildings.find((building: any) => building.id === parseInt(selectedBuilding)),
          "buildingName",
        ) || ""
      : ""

    return {
      cityProject: selectedCity && selectedProject ? `${selectedCity} - ${selectedProject}` : "",
      building: selectedBuildingName,
      destination: selectedDestination || "",
      files: "",
    }
  }

  const filterFilesByCityProject = () => {
    if (!fileManagementSteps || !selectedCity || !selectedProject) return []
    return fileManagementSteps.filter((file: any) => {
      const city = getFileCity(file)
      const projectValue = getFileProject(file)
      return city === selectedCity && projectValue === selectedProject
    })
  }

  const filterFilesByBuilding = (files: any[]) => {
    if (!selectedBuilding) return []
    const selectedBuildingObj = allBuildings.find(
      (b: any) => b.id === parseInt(selectedBuilding),
    )
    const selectedBuildingName = selectedBuildingObj?.name
    if (!selectedBuildingName) return []
    return files.filter((file: any) => {
      const buildingSelection = extractProperty(file, "buildingSelection")?.[0]
      const buildingSelectionValue = extractProperty(buildingSelection, "BuildingSelection")
      if (Array.isArray(buildingSelectionValue)) {
        return buildingSelectionValue.some(
          (building: any) => building.name === selectedBuildingName,
        )
      }
      return false
    })
  }

  const filterFilesByDestination = (files: any[]) => {
    return files.filter((file: any) => {
      const destination = extractProperty(file, "fileDestination")?.[0]
      const destinationValue = extractProperty(destination, "fileDestination")
      return destinationValue === selectedDestination
    })
  }

  const groupAllFilesByCategory = (files: any[]) => {
    return files.reduce((acc: any, file: any) => {
      const categories = extractProperty(file, "selectCategory")?.[0]
      const categoriesValue = extractProperty(categories, "selectCategory")
      if (Array.isArray(categoriesValue)) {
        categoriesValue.forEach((category: string) => {
          if (!acc[category]) acc[category] = []
          acc[category].push(file)
        })
      } else {
        const category = categoriesValue || "Other"
        if (!acc[category]) acc[category] = []
        acc[category].push(file)
      }
      return acc
    }, {})
  }

  const handleButtonClick = () => {
    switch (value) {
      case 0: {
        if (!selectedCity || !selectedProject) {
          setHasError(true)
          break
        }
        const cityProjectFiltered = filterFilesByCityProject()
        setFilteredByCityProject(cityProjectFiltered)
        setFilteredFiles(cityProjectFiltered)
        setValue(1)
        setHasError(false)
        break
      }
      case 1: {
        if (!selectedBuilding) {
          setHasError(true)
          break
        }
        const buildingFiltered = filterFilesByBuilding(filteredFiles)
        setFilteredByBuilding(buildingFiltered)
        setFilteredFiles(buildingFiltered)
        setValue(2)
        setHasError(false)
        break
      }
      case 2: {
        if (!selectedDestination) {
          setHasError(true)
          break
        }
        const destinationFiltered = filterFilesByDestination(filteredFiles)
        setFilteredFiles(destinationFiltered)
        const grouped = groupAllFilesByCategory(destinationFiltered)
        setGroupedFiles(grouped)
        setValue(3)
        setHasError(false)
        break
      }
    }
  }

  const handleGoBack = () => {
    setHasError(false)
    setTimeout(() => {
      switch (value) {
        case 1:
          setSelectedBuilding(null)
          setFilteredFiles([])
          setValue(0)
          break
        case 2:
          setDestination(null)
          setFilteredFiles(filteredByCityProject)
          setValue(1)
          break
        case 3:
          setFilteredFiles(filteredByBuilding)
          setGroupedFiles({})
          setValue(2)
          break
      }
    }, 300)
  }

  const isButtonActive =
    (value === 0 && selectedCity && selectedProject) ||
    (value === 1 && selectedBuilding) ||
    (value === 2 && selectedDestination)

  return {
    value,
    selectedCity,
    selectedProject,
    cities,
    projectsForCity,
    selectedBuilding,
    filteredFiles,
    selectedDestination,
    groupedFiles,
    hasError,
    handleCityChange,
    handleProjectChange,
    handleBuildingChange,
    handleDestinationChange,
    handleButtonClick,
    handleGoBack,
    getSelectedDisplayValues,
    isButtonActive,
  }
}
